import type { EventStore } from "applesauce-core";
import {
  setHiddenTagsEncryptionMethod,
  unlockHiddenTags,
} from "applesauce-core/helpers";
import type { ISigner } from "applesauce-signers";
import type { NostrEvent } from "nostr-tools";
import { BehaviorSubject, type Subscription } from "rxjs";
import { normalizeUrl } from "@/lib/url";
import {
  COMMENT_KIND,
  COVER_NOTE_KIND,
  ISSUE_KIND,
  LABEL_KIND,
  LEGACY_REPLY_KIND,
  PATCH_KIND,
  PR_KIND,
  PR_UPDATE_KIND,
  REPO_KIND,
  STATUS_KINDS,
  resolveChain,
} from "@/lib/nip34";
import { CI_EVENT_KINDS, CI_MANUAL_TRIGGER_KIND } from "@/lib/ci";

export const PRIVATE_GIT_RELAY_LIST_KIND = 10318;

// GRASP-08 requires NIP-44 for every hidden item in this NIP-51 list.
setHiddenTagsEncryptionMethod(PRIVATE_GIT_RELAY_LIST_KIND, "nip44");

const activePubkey$ = new BehaviorSubject<string | undefined>(undefined);
export const privateGitRelayUrls$ = new BehaviorSubject<string[]>([]);
export const verifiedPrivateGitCloneUrls$ = new BehaviorSubject<string[]>([]);
const verifiedPrivateGitRelayByCloneUrl = new Map<string, string>();

// Keep this quarantine for the lifetime of the page. Removing an account must
// not make events fetched under that account eligible for cache reads/writes.
const privateEventIds = new Set<string>();
const privateRepositoryCoordinates = new Set<string>();

const REPO_ANNOUNCEMENT_KIND = 30617;
const REPO_STATE_KIND = 30618;
const ALWAYS_REPOSITORY_RELATED_KINDS = new Set<number>([
  REPO_ANNOUNCEMENT_KIND,
  REPO_STATE_KIND,
  ISSUE_KIND,
  PATCH_KIND,
  PR_KIND,
  PR_UPDATE_KIND,
  COMMENT_KIND,
  COVER_NOTE_KIND,
  LEGACY_REPLY_KIND,
  LABEL_KIND,
  ...STATUS_KINDS,
  ...CI_EVENT_KINDS,
  CI_MANUAL_TRIGGER_KIND,
]);
const REFERENCE_ONLY_REPOSITORY_KINDS = new Set([
  1, // legacy generic reply
  5, // deletion
  7, // reaction/star
  9735, // zap receipt
]);

function eventIdentifier(event: NostrEvent): string | undefined {
  return event.tags.find(([name]) => name === "d")?.[1];
}

function eventRepositoryCoordinates(event: NostrEvent): string[] {
  const coordinates = event.tags
    .filter(([name, value]) => (name === "a" || name === "A") && Boolean(value))
    .map(([, value]) => value);

  if (event.kind === REPO_ANNOUNCEMENT_KIND || event.kind === REPO_STATE_KIND) {
    const identifier = eventIdentifier(event);
    if (identifier) {
      coordinates.push(
        `${REPO_ANNOUNCEMENT_KIND}:${event.pubkey}:${identifier}`,
      );
    }
  }

  return coordinates;
}

/**
 * Repository-related events are deliberately session-only.
 *
 * An announcement that looks public today can become private after a missing
 * recursive maintainer announcement arrives. Keeping the whole NIP-34 graph
 * out of persistent storage makes that transition safe even for cache
 * backends whose pending write queue cannot be cancelled.
 */
export function isRepositoryRelatedEvent(event: NostrEvent): boolean {
  if (ALWAYS_REPOSITORY_RELATED_KINDS.has(event.kind)) {
    return true;
  }

  return event.tags.some(([name, value]) => {
    if ((name === "a" || name === "A") && value) {
      return value.startsWith(`${REPO_ANNOUNCEMENT_KIND}:`);
    }
    if ((name === "k" || name === "K") && value) {
      return value === String(REPO_ANNOUNCEMENT_KIND);
    }
    if (
      (name === "e" || name === "E") &&
      value &&
      REFERENCE_ONLY_REPOSITORY_KINDS.has(event.kind)
    ) {
      return true;
    }
    return false;
  });
}

/** Compute the exact service-relay union still used by private repositories. */
export function collectPrivateGitRelayUrls(
  announcements: NostrEvent[],
): string[] {
  const urls = new Set<string>();
  const seen = new Set<string>();

  for (const announcement of announcements) {
    if (announcement.kind !== REPO_KIND) continue;
    const identifier = eventIdentifier(announcement);
    if (!identifier) continue;
    const coordinate = `${announcement.pubkey}:${identifier}`;
    if (seen.has(coordinate)) continue;
    seen.add(coordinate);

    const resolved = resolveChain(
      announcements,
      announcement.pubkey,
      identifier,
    );
    if (!resolved?.isPrivate) continue;
    for (const relayUrl of resolved.relays) {
      urls.add(normalizeUrl(relayUrl));
    }
  }

  return [...urls].sort();
}

function parsePrivateGitRelayTags(tags: string[][]): string[] {
  const urls: string[] = [];

  for (const tag of tags) {
    if (tag.length !== 2 || tag[0] !== "g" || !tag[1]) {
      throw new Error("Private Git relay list contains a non-g item");
    }

    const parsed = new URL(tag[1]);
    if (
      (parsed.protocol !== "ws:" && parsed.protocol !== "wss:") ||
      parsed.username ||
      parsed.password ||
      parsed.hash
    ) {
      throw new Error("Private Git relay list contains an invalid relay URL");
    }
    urls.push(normalizeUrl(parsed.toString()));
  }

  return [...new Set(urls)];
}

export function beginPrivateRepositorySession(pubkey?: string): string[] {
  const previousUrls = privateGitRelayUrls$.getValue();
  activePubkey$.next(pubkey);
  privateGitRelayUrls$.next([]);
  verifiedPrivateGitRelayByCloneUrl.clear();
  verifiedPrivateGitCloneUrls$.next([]);
  privateRepositoryCoordinates.clear();
  return previousUrls;
}

function emitVerifiedPrivateGitCloneUrls(): void {
  verifiedPrivateGitCloneUrls$.next(
    [...verifiedPrivateGitRelayByCloneUrl.keys()].sort(),
  );
}

export function markVerifiedPrivateGitCloneUrl(
  url: string,
  relayUrl: string,
): void {
  const normalized = normalizeUrl(url);
  const normalizedRelay = normalizeUrl(relayUrl);
  if (verifiedPrivateGitRelayByCloneUrl.get(normalized) !== normalizedRelay) {
    verifiedPrivateGitRelayByCloneUrl.set(normalized, normalizedRelay);
    emitVerifiedPrivateGitCloneUrls();
  }
}

export function isVerifiedPrivateGitCloneUrl(url: string): boolean {
  return verifiedPrivateGitRelayByCloneUrl.has(normalizeUrl(url));
}

export function getVerifiedPrivateGitRelayUrl(
  cloneUrl: string,
): string | undefined {
  return verifiedPrivateGitRelayByCloneUrl.get(normalizeUrl(cloneUrl));
}

function urlsHaveSameHost(left: string, right: string): boolean {
  try {
    return new URL(left).host === new URL(right).host;
  } catch {
    return false;
  }
}

/**
 * Drop credentials for endpoints that are no longer both announced by a
 * recursively-private repository and paired with a currently trusted kind
 * 10318 relay.
 */
export function reconcileVerifiedPrivateGitCloneUrls(
  announcements: NostrEvent[],
): void {
  if (verifiedPrivateGitRelayByCloneUrl.size === 0) return;

  const trustedRelays = new Set(
    privateGitRelayUrls$.getValue().map(normalizeUrl),
  );
  const allowedPairs = new Set<string>();
  const seen = new Set<string>();

  for (const announcement of announcements) {
    if (announcement.kind !== REPO_KIND) continue;
    const identifier = eventIdentifier(announcement);
    if (!identifier) continue;
    const coordinate = `${announcement.pubkey}:${identifier}`;
    if (seen.has(coordinate)) continue;
    seen.add(coordinate);

    const resolved = resolveChain(
      announcements,
      announcement.pubkey,
      identifier,
    );
    if (!resolved?.isPrivate) continue;

    for (const cloneUrl of resolved.graspCloneUrls) {
      const normalizedClone = normalizeUrl(cloneUrl);
      for (const relayUrl of resolved.relays) {
        const normalizedRelay = normalizeUrl(relayUrl);
        if (
          trustedRelays.has(normalizedRelay) &&
          urlsHaveSameHost(normalizedClone, normalizedRelay)
        ) {
          allowedPairs.add(`${normalizedClone}\n${normalizedRelay}`);
        }
      }
    }
  }

  let changed = false;
  for (const [cloneUrl, relayUrl] of verifiedPrivateGitRelayByCloneUrl) {
    if (!allowedPairs.has(`${cloneUrl}\n${relayUrl}`)) {
      verifiedPrivateGitRelayByCloneUrl.delete(cloneUrl);
      changed = true;
    }
  }
  if (changed) emitVerifiedPrivateGitCloneUrls();
}

export function isActivePrivateGitRelay(url: string): boolean {
  const normalized = normalizeUrl(url);
  return privateGitRelayUrls$.getValue().includes(normalized);
}

export function markPrivateRelayEvent(event: NostrEvent): void {
  privateEventIds.add(event.id);
}

/**
 * Replace the active session's resolved private repository coordinates.
 *
 * Event IDs remain quarantined for the page lifetime, but coordinates are
 * account-scoped and must be rebuilt after an account switch.
 */
export function replacePrivateRepositoryCoordinates(
  coordinates: Iterable<string>,
): void {
  privateRepositoryCoordinates.clear();
  for (const coordinate of coordinates) {
    privateRepositoryCoordinates.add(coordinate);
  }
}

export function isPrivateRepositoryCoordinate(coordinate: string): boolean {
  return privateRepositoryCoordinates.has(coordinate);
}

export function isQuarantinedPrivateEventId(eventId: string): boolean {
  return privateEventIds.has(eventId);
}

/**
 * Synchronously classify an event from its repository coordinates or a
 * reference to an already quarantined event.
 */
export function classifyPrivateRepositoryEvent(event: NostrEvent): boolean {
  if (
    event.kind === REPO_ANNOUNCEMENT_KIND &&
    event.tags.some(
      ([name, value, ...extra]) =>
        name === "private" && value === "true" && extra.length === 0,
    )
  ) {
    privateEventIds.add(event.id);
    return true;
  }

  const isPrivate =
    eventRepositoryCoordinates(event).some((coordinate) =>
      privateRepositoryCoordinates.has(coordinate),
    ) ||
    event.tags.some(
      ([name, value]) =>
        (name === "e" || name === "E") &&
        Boolean(value) &&
        privateEventIds.has(value),
    );

  if (isPrivate) privateEventIds.add(event.id);
  return isPrivate;
}

export function isPrivateRelayEvent(event: NostrEvent): boolean {
  return privateEventIds.has(event.id) || classifyPrivateRepositoryEvent(event);
}

export function getPrivateRelayEventIds(): string[] {
  return [...privateEventIds];
}

/**
 * Build a kind 10318 replacement containing the exact supplied relay union.
 *
 * Existing hidden items are decrypted and preserved. Refusing to overwrite an
 * invalid or undecryptable replacement prevents one client from silently
 * erasing private repositories created by another client.
 */
export async function createPrivateGitRelayListUpdate(
  store: EventStore,
  pubkey: string,
  signer: ISigner,
  relayUrls: string[] | (() => string[]),
  refreshLatest: () => Promise<void>,
  refreshRepositories: (relayUrls: string[]) => Promise<void>,
  removeRelayUrls: string[] | (() => string[]) = [],
): Promise<NostrEvent | undefined> {
  if (!signer.nip44) {
    throw new Error(
      "Private repositories require a signer that supports NIP-44 encryption",
    );
  }

  // A replaceable write based only on a warm EventStore can silently erase a
  // newer event from another client. Require a fresh all-relay EOSE first.
  await refreshLatest();
  const existing = store.getReplaceable(PRIVATE_GIT_RELAY_LIST_KIND, pubkey);
  let existingUrls: string[] = [];
  if (existing) {
    if (
      existing.pubkey !== pubkey ||
      existing.kind !== PRIVATE_GIT_RELAY_LIST_KIND ||
      existing.tags.length !== 0
    ) {
      throw new Error(
        "Existing private Git relay list has an invalid envelope",
      );
    }
    existingUrls = parsePrivateGitRelayTags(
      await unlockHiddenTags(existing, signer, "nip44"),
    );
  }
  if (activePubkey$.getValue() === pubkey) {
    privateGitRelayUrls$.next(existingUrls);
    reconcileVerifiedPrivateGitCloneUrls(
      store.getByFilters({ kinds: [REPO_KIND] }),
    );
  }
  await refreshRepositories(existingUrls);

  const desiredUrls = parsePrivateGitRelayTags(
    (typeof relayUrls === "function" ? relayUrls() : relayUrls).map((url) => [
      "g",
      url,
    ]),
  );
  const removed = new Set(
    parsePrivateGitRelayTags(
      (typeof removeRelayUrls === "function"
        ? removeRelayUrls()
        : removeRelayUrls
      ).map((url) => ["g", url]),
    ),
  );
  const nextUrls = reconcilePrivateGitRelayUrls(
    existingUrls,
    desiredUrls,
    removed,
  );
  existingUrls.sort();
  if (
    existing &&
    nextUrls.length === existingUrls.length &&
    nextUrls.every((url, index) => url === existingUrls[index])
  ) {
    return undefined;
  }

  const content = await signer.nip44.encrypt(
    pubkey,
    JSON.stringify(nextUrls.map((url) => ["g", url])),
  );
  const signed = await signer.signEvent({
    kind: PRIVATE_GIT_RELAY_LIST_KIND,
    content,
    tags: [],
    created_at: Math.max(
      Math.floor(Date.now() / 1000),
      (existing?.created_at ?? 0) + 1,
    ),
  });
  if (activePubkey$.getValue() === pubkey) {
    privateGitRelayUrls$.next(nextUrls);
    reconcileVerifiedPrivateGitCloneUrls(
      store.getByFilters({ kinds: [REPO_KIND] }),
    );
  }
  return signed;
}

export function reconcilePrivateGitRelayUrls(
  existingUrls: string[],
  desiredUrls: string[],
  removedUrls: Iterable<string>,
): string[] {
  const desired = new Set(desiredUrls);
  const removed = new Set(removedUrls);
  return [
    ...new Set([
      ...existingUrls.filter((url) => !removed.has(url) || desired.has(url)),
      ...desiredUrls,
    ]),
  ].sort();
}

/**
 * Watch the active account's replaceable kind 10318 event and expose its
 * strictly validated hidden `g` items. Invalid or undecryptable replacements
 * clear the set so a revoked relay is never retained from an older version.
 */
export function watchPrivateGitRelayList(
  store: EventStore,
  pubkey: string,
  signer: ISigner,
): () => void {
  let generation = 0;

  const subscription: Subscription = store
    .replaceable(PRIVATE_GIT_RELAY_LIST_KIND, pubkey)
    .subscribe((event) => {
      const currentGeneration = ++generation;
      if (activePubkey$.getValue() !== pubkey) return;
      if (!event) {
        privateGitRelayUrls$.next([]);
        reconcileVerifiedPrivateGitCloneUrls(
          store.getByFilters({ kinds: [REPO_KIND] }),
        );
        return;
      }

      void (async () => {
        try {
          if (
            event.pubkey !== pubkey ||
            event.kind !== PRIVATE_GIT_RELAY_LIST_KIND ||
            event.tags.length !== 0
          ) {
            throw new Error("Invalid private Git relay list envelope");
          }
          const tags = await unlockHiddenTags(event, signer, "nip44");
          const urls = parsePrivateGitRelayTags(tags);
          if (
            activePubkey$.getValue() === pubkey &&
            currentGeneration === generation
          ) {
            privateGitRelayUrls$.next(urls);
            reconcileVerifiedPrivateGitCloneUrls(
              store.getByFilters({ kinds: [REPO_KIND] }),
            );
          }
        } catch (err) {
          if (
            activePubkey$.getValue() === pubkey &&
            currentGeneration === generation
          ) {
            privateGitRelayUrls$.next([]);
            reconcileVerifiedPrivateGitCloneUrls(
              store.getByFilters({ kinds: [REPO_KIND] }),
            );
            console.warn("[private-repos] Failed to unlock kind 10318:", err);
          }
        }
      })();
    });

  return () => {
    generation += 1;
    subscription.unsubscribe();
  };
}
